import { useState, useEffect } from "react";
import { toast } from "sonner";
import { ShieldCheck } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { useApp } from "@/context/AppContext";
import { api, uploadImage, track, syncSaved } from "@/lib/api";
import { errorKey } from "@/lib/i18n";
import { AnkaraStrip, Avatar, CategoryIcon } from "./ui";
import { PhoneInput } from "./PhoneInput";

const btn = "press w-full min-h-[52px] rounded-full font-extrabold";
const field = "w-full min-h-[48px] rounded-2xl border border-[#E8E2D9] bg-[#FAF7F2] px-4 text-base";

export const CatChips = ({ value, onChange, testid = "alert-cat" }) => {
  const { meta, L } = useApp();
  const toggle = (id) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  return (
    <div className="flex flex-wrap gap-2">
      {meta.categories.map((c) => (
        <button type="button" key={c.id} data-testid={`${testid}-${c.id}`} aria-pressed={value.includes(c.id)} onClick={() => toggle(c.id)}
          className={`press inline-flex items-center gap-1.5 min-h-[40px] px-3 rounded-full text-sm font-semibold border ${value.includes(c.id) ? "bg-[#C2410C] text-white border-[#C2410C]" : "bg-[#FFFDF9] border-[#E8E2D9]"}`}>
          <CategoryIcon icon={c.icon} className="w-4 h-4" />{L(c)}
        </button>
      ))}
    </div>
  );
};

const Signup = ({ initialPhone, onSent }) => {
  const { t, lang, hub, meta, L, areasOf } = useApp();
  const [f, setF] = useState({ name: "", phone: initialPhone || "", city_hub_id: hub?.id, area_id: "", preferred_language: lang, alert_categories: [] });
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const { data } = await api.post("/auth/signup", { ...f, area_id: f.area_id || null, ref: localStorage.getItem("dm_ref") || null });
      localStorage.removeItem("dm_ref");
      if (data.token) localStorage.setItem("dm_token", data.token);
      if (photo && data.token) { try { const path = await uploadImage(photo); await api.put("/me", { photo_url: path }); } catch { /* optional */ } }
      onSent(f.phone, data.dev_code, data.token ? data : null);
    } catch (err) { toast.error(t(errorKey(err))); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="signup-form">
      <div className="flex items-center gap-3">
        <Avatar user={{ name: f.name || "?", photo_url: null }} size={52} />
        <label className="text-sm font-semibold text-[#1E4D3B] underline cursor-pointer">{t("profile_photo")} ({t("optional")})
          <input data-testid="signup-photo" type="file" accept="image/*" className="hidden" onChange={(e) => setPhoto(e.target.files?.[0])} />
        </label>
        {photo && <span className="text-xs text-[#6B5E55] truncate">{photo.name}</span>}
      </div>
      <input data-testid="signup-name" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder={t("name")} className={field} />
      <PhoneInput value={f.phone} onChange={(v) => setF((s) => ({ ...s, phone: v }))} testid="signup-phone" />
      <div className="grid grid-cols-2 gap-2">
        <select data-testid="signup-hub" value={f.city_hub_id} onChange={(e) => setF({ ...f, city_hub_id: e.target.value, area_id: "" })} className={field} aria-label={t("city_hub")}>
          {meta.hubs.map((h) => <option key={h.id} value={h.id}>{L(h)}</option>)}
        </select>
        <select data-testid="signup-area" value={f.area_id} onChange={(e) => setF({ ...f, area_id: e.target.value })} className={field} aria-label={t("area")}>
          <option value="">{t("area")} ({t("optional")})</option>
          {areasOf(f.city_hub_id).map((a) => <option key={a.id} value={a.id}>{L(a)}</option>)}
        </select>
      </div>
      <div className="flex gap-2">
        {["en", "fr"].map((l) => <button type="button" key={l} data-testid={`signup-lang-${l}`} onClick={() => setF({ ...f, preferred_language: l })} className={`press flex-1 min-h-[44px] rounded-full font-bold ${f.preferred_language === l ? "bg-[#1E4D3B] text-white" : "bg-[#F3EDE4]"}`}>{l === "en" ? "English" : "Français"}</button>)}
      </div>
      <div><div className="text-sm font-semibold mb-2">{t("alert_cats")} ({t("optional")})</div><CatChips value={f.alert_categories} onChange={(v) => setF({ ...f, alert_categories: v })} /></div>
      <button disabled={busy} data-testid="signup-submit" className={`${btn} bg-[#F59E0B] text-[#291E18]`}>{t("continue")}</button>
    </form>
  );
};

const Login = ({ onSent, onNoAccount }) => {
  const { t } = useApp();
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await api.post("/auth/login", { phone }); onSent(phone, data.dev_code); }
    catch (err) { const k = errorKey(err); if (k === "no_account") { toast(t("no_account")); onNoAccount(phone); } else toast.error(t(k)); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="login-form">
      <PhoneInput value={phone} onChange={setPhone} testid="login-phone" />
      <button disabled={busy} data-testid="login-submit" className={`${btn} bg-[#1E4D3B] text-white`}>{t("send_code")}</button>
    </form>
  );
};

const Otp = ({ phone, devCode, onDone, canSkip }) => {
  const { t, login, setAuth } = useApp();
  const [code, setCode] = useState("");
  const [hint, setHint] = useState(devCode);
  const [busy, setBusy] = useState(false);
  const verify = async (c) => {
    setBusy(true);
    try { const { data } = await api.post("/auth/verify", { phone, code: c }); login(data.token, data.user); toast.success(data.newly_verified ? t("verified_ok") : t("welcome_back", { name: data.user.name.split(" ")[0] })); onDone(); }
    catch (err) { toast.error(t(errorKey(err))); setCode(""); }
    setBusy(false);
  };
  const resend = async () => {
    try { const { data } = await api.post("/auth/login", { phone }); setHint(data.dev_code); } catch (err) { toast.error(t(errorKey(err))); }
  };
  return (
    <div className="space-y-4" data-testid="otp-step">
      <p className="text-sm text-[#6B5E55]">{t("enter_code", { phone })}</p>
      {hint && <p data-testid="dev-code-hint" className="text-sm rounded-xl bg-[#FEF3C7] text-[#78350F] p-3 font-semibold">{t("dev_code", { code: hint })}</p>}
      <div className="flex justify-center">
        <InputOTP data-testid="otp-input" maxLength={6} value={code} onChange={(v) => { setCode(v); if (v.length === 6) verify(v); }} inputMode="numeric">
          <InputOTPGroup>{[0, 1, 2, 3, 4, 5].map((i) => <InputOTPSlot key={i} index={i} className="w-11 h-14 text-xl font-bold bg-[#FAF7F2]" />)}</InputOTPGroup>
        </InputOTP>
      </div>
      <button disabled={busy || code.length < 6} onClick={() => verify(code)} data-testid="otp-verify" className={`${btn} bg-[#1E4D3B] text-white disabled:opacity-50`}>{t("verify")}</button>
      <div className="flex justify-between">
        <button data-testid="otp-resend" onClick={resend} className="text-sm font-semibold text-[#1E4D3B] underline min-h-[44px]">{t("resend")}</button>
        {canSkip && <button data-testid="otp-skip" onClick={() => setAuth(null)} className="text-sm font-semibold text-[#6B5E55] min-h-[44px]">{t("verify_later")}</button>}
      </div>
    </div>
  );
};

const VerifyStart = ({ onSent }) => {
  const { t, user } = useApp();
  const send = async () => {
    try { const { data } = await api.post("/auth/resend"); onSent(user.phone, data.dev_code); } catch (err) { toast.error(t(errorKey(err))); }
  };
  return (
    <div className="space-y-4">
      <div className="flex gap-3 items-start"><ShieldCheck className="w-8 h-8 text-[#16A34A] shrink-0" /><p className="text-[#291E18]">{t("verify_body")}</p></div>
      <button data-testid="verify-send-code" onClick={send} className={`${btn} bg-[#F59E0B] text-[#291E18]`}>{t("send_code")}</button>
    </div>
  );
};

export const AuthDialog = () => {
  const { auth, setAuth, t, refreshUser, setUser } = useApp();
  const [mode, setMode] = useState("prompt");
  const [phone, setPhone] = useState("");
  const [devCode, setDevCode] = useState(null);
  const [fromSignup, setFromSignup] = useState(false);
  useEffect(() => { if (auth) { setMode(auth.mode); setFromSignup(false); } }, [auth]);
  const sent = (p, code, signupData) => {
    setPhone(p); setDevCode(code); setMode("otp");
    if (signupData) { setFromSignup(true); setUser(signupData.user); syncSaved(true).then(refreshUser); }
  };
  const titles = { prompt: t("create_account"), signup: t("create_account"), login: t("log_in"), otp: t("verify_title"), verify: t("verify_title") };
  return (
    <Dialog open={!!auth} onOpenChange={(o) => !o && setAuth(null)}>
      <DialogContent data-testid="auth-dialog" className="p-0 overflow-hidden rounded-[24px] bg-[#FFFDF9] max-w-md max-h-[92vh] overflow-y-auto">
        <AnkaraStrip />
        <div className="p-6 space-y-4">
          <DialogTitle className="text-2xl font-extrabold">{titles[mode]}</DialogTitle>
          <DialogDescription className="sr-only">Diaspomark account</DialogDescription>
          {mode === "prompt" && (
            <div className="space-y-4" data-testid="signup-prompt">
              <p className="text-[#291E18] text-base">{t("signup_prompt")}</p>
              <button data-testid="prompt-signup" onClick={() => { setMode("signup"); track("signup_started"); }} className={`${btn} bg-[#F59E0B] text-[#291E18]`}>{t("create_account")}</button>
              <button data-testid="prompt-login" onClick={() => setMode("login")} className={`${btn} border-2 border-[#1E4D3B] text-[#1E4D3B]`}>{t("log_in")}</button>
            </div>
          )}
          {mode === "signup" && <><Signup initialPhone={phone} onSent={sent} /><button data-testid="switch-login" onClick={() => setMode("login")} className="w-full text-sm font-semibold text-[#1E4D3B] min-h-[44px]">{t("log_in")}</button></>}
          {mode === "login" && <><Login onSent={sent} onNoAccount={(p) => { setPhone(p); setMode("signup"); }} /><button data-testid="switch-signup" onClick={() => setMode("signup")} className="w-full text-sm font-semibold text-[#1E4D3B] min-h-[44px]">{t("create_account")}</button></>}
          {mode === "verify" && <VerifyStart onSent={sent} />}
          {mode === "otp" && <Otp phone={phone} devCode={devCode} canSkip={fromSignup} onDone={() => setAuth(null)} />}
        </div>
      </DialogContent>
    </Dialog>
  );
};
